import type { Member, Reaction } from "@bundle/contracts";
import { Emoji, Messages } from "@bundle/core";
import { useState } from "react";
import { EmojiPicker, IconButton, Popover, Pressable, Stack, Text } from "../primitives/index.ts";
import { useLocale, useT, useTr } from "../i18n/I18nProvider.tsx";

export type ReactionChipsProps = {
  reactions: Reaction[];
  meId: string;
  /** Names for the "who reacted" tooltip (unknown ids show as-is). */
  members: Record<string, Member>;
  /** The reaction I just added: once its chip shows it as mine, it pops and scrolls into view. */
  attention?: { emoji: string; seq: number };
  onToggle: (emoji: string) => void;
  testID: string;
};

/**
 * Pill chips (emoji + count; mine accent-tinted and pressed) that toggle my reaction (click, Enter or Space), show
 * who reacted on hover/focus, and an add-reaction button with the emoji picker.
 */
export function ReactionChips({ reactions, meId, members, attention, onToggle, testID }: ReactionChipsProps) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  const [picking, setPicking] = useState(false);
  if (reactions.length === 0) return null;
  return (
    <Stack direction="horizontal" align="center" gap="1" wrap testID={testID}>
      {reactions.map((r) => {
        const mine = r.userIds.includes(meId);
        const who = tr(Messages.reactorsLabel(r, members, meId));
        return (
          <Pressable
            key={r.emoji}
            radius="full"
            selected={mine}
            onPress={() => onToggle(r.emoji)}
            accessibilityLabel={who}
            tooltip={who}
            attentionKey={mine && attention !== undefined && attention.emoji === r.emoji ? attention.seq : undefined}
            testID={`${testID}-${r.emoji}`}
          >
            <Stack direction="horizontal" align="center" gap="1" paddingX="2" radius="full" background={mine ? "accent.subtle" : undefined} border={mine ? "accent.text" : "border.subtle"}>
              <Text variant="footnote">{Emoji.displayEmoji(r.emoji)}</Text>
              <Text variant="caption" weight="semibold" color={mine ? "text.link" : "text.secondary"}>
                {String(r.count)}
              </Text>
            </Stack>
          </Pressable>
        );
      })}
      <Popover
        open={picking}
        onClose={() => setPicking(false)}
        anchor={<IconButton icon="reaction" size="sm" accessibilityLabel={t("message.addReaction")} onPress={() => setPicking(true)} testID={`${testID}-add`} />}
      >
        <EmojiPicker
          locale={locale}
          searchPlaceholder={t("picker.emoji.search")}
          report={false}
          onSelect={(e) => {
            setPicking(false);
            onToggle(e);
          }}
        />
      </Popover>
    </Stack>
  );
}
